import React, { useState } from 'react';
import { apiFetch } from '../../../../lib/api';
import { API_DOCS } from './TabAnagrafica.api';
import ApiInspector from '../../../../components/ApiInspector';
import { PAESI } from '../../../../config/paesi';

// ── Icons ─────────────────────────────────────────────────────────────────
const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

// ── Interfaces ────────────────────────────────────────────────────────────
interface Indirizzo {
  id: number;
  tipo: 'SEDE_LEGALE' | 'SEDE_OPERATIVA' | 'SPEDIZIONE';
  nome?: string;
  via?: string;
  cap?: string;
  citta?: string;
  provincia?: string;
  paese?: string;
  principale: boolean;
}

interface Email {
  id: number;
  etichetta: string;
  email: string;
  principale: boolean;
  ambito?: 'GENERALE' | 'CLIENTE' | 'FORNITORE';
}

interface Contatto {
  id: number;
  nome: string;
  cognome?: string;
  ruolo?: string;
  telefono?: string;
  cellulare?: string;
  email?: string;
  principale: boolean;
  ambito?: 'GENERALE' | 'CLIENTE' | 'FORNITORE';
}

interface Filiale {
  id: number;
  nome: string;
  via?: string;
  cap?: string;
  citta?: string;
  provincia?: string;
  paese?: string;
  note?: string;
  attivo: boolean;
}

interface Nominativo {
  id: number;
  tipoSoggetto: 'AZIENDA' | 'PRIVATO';
  ragioneSociale?: string;
  nome?: string;
  cognome?: string;
  partitaIva?: string;
  codiceFiscale?: string;
  codiceSDI?: string;
  pec?: string;
  telefono?: string;
  emailPrincipale?: string;
  sito?: string;
  note?: string;
  indirizzi?: Indirizzo[];
  email?: Email[];
  contatti?: Contatto[];
  filiali?: Filiale[];
}

interface TabAnagraficaProps {
  nominativo: Nominativo;
  onReload: () => void;
}

// ── Componente ────────────────────────────────────────────────────────────
export default function TabAnagrafica({ nominativo, onReload }: TabAnagraficaProps) {
  // ── Stato modale indirizzo ─────────────────────────────────────────────
  const [showIndirizzoModal, setShowIndirizzoModal] = useState(false);
  const [editingIndirizzo, setEditingIndirizzo] = useState<Indirizzo | null>(null);
  const [savingIndirizzo, setSavingIndirizzo] = useState(false);
  const [indirizzoForm, setIndirizzoForm] = useState({
    tipo: 'SEDE_LEGALE' as 'SEDE_LEGALE' | 'SEDE_OPERATIVA' | 'SPEDIZIONE',
    nome: '',
    via: '',
    cap: '',
    citta: '',
    provincia: '',
    paese: 'Italia',
    principale: false,
  });

  // ── Utils ─────────────────────────────────────────────────────────────────
  const getTipoIndirizzoLabel = (tipo: string) => {
    switch (tipo) {
      case 'SEDE_LEGALE': return 'Sede Legale';
      case 'SEDE_OPERATIVA': return 'Sede Operativa';
      case 'SPEDIZIONE': return 'Spedizione';
      default: return tipo;
    }
  };

  const formatIndirizzo = (ind: Indirizzo) => {
    const parts: string[] = [];
    if (ind.via) parts.push(ind.via);
    if (ind.cap) parts.push(ind.cap);
    if (ind.citta) parts.push(ind.citta);
    if (ind.provincia) parts.push(`(${ind.provincia})`);
    if (ind.paese && ind.paese !== 'Italia') parts.push(ind.paese);
    return parts.join(' ') || '—';
  };

  const renderAmbitoBadge = (ambito?: string) => {
    const a = ambito || 'GENERALE';
    if (a === 'CLIENTE') return <span className="bop-badge bop-badge--info">Cliente</span>;
    if (a === 'FORNITORE') return <span className="bop-badge bop-badge--warning">Fornitore</span>;
    return <span className="bop-badge bop-badge--neutral">Generale</span>;
  };

  // ── Helper protezione unica SEDE_LEGALE ───────────────────────────────
  const isUnicaSedeLegale = (ind: Indirizzo): boolean => {
    if (ind.tipo !== 'SEDE_LEGALE') return false;
    const sediLegali = (nominativo.indirizzi || []).filter(i => i.tipo === 'SEDE_LEGALE');
    return sediLegali.length === 1;
  };

  // ── Handlers modale indirizzo ─────────────────────────────────────────
  const openIndirizzoModal = (ind?: Indirizzo) => {
    if (ind) {
      setEditingIndirizzo(ind);
      setIndirizzoForm({
        tipo: ind.tipo,
        nome: ind.nome || '',
        via: ind.via || '',
        cap: ind.cap || '',
        citta: ind.citta || '',
        provincia: ind.provincia || '',
        paese: ind.paese || 'Italia',
        principale: ind.principale,
      });
    } else {
      setEditingIndirizzo(null);
      setIndirizzoForm({
        tipo: 'SEDE_LEGALE',
        nome: '',
        via: '',
        cap: '',
        citta: '',
        provincia: '',
        paese: 'Italia',
        principale: false,
      });
    }
    setShowIndirizzoModal(true);
  };

  const saveIndirizzo = async () => {
    if (!indirizzoForm.citta.trim()) {
      alert('La città è obbligatoria');
      return;
    }
    setSavingIndirizzo(true);
    try {
      if (editingIndirizzo) {
        // @api PUT /api/nominativi/:id/indirizzi/:indirizzoId {"tipo":"string","nome":"string","via":"string","cap":"string","citta":"string","provincia":"string","paese":"string","principale":"boolean"} -> {"id":"number"}
        await apiFetch(`/api/nominativi/${nominativo.id}/indirizzi/${editingIndirizzo.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(indirizzoForm),
        });
      } else {
        // @api POST /api/nominativi/:id/indirizzi {"tipo":"string","nome":"string","via":"string","cap":"string","citta":"string","provincia":"string","paese":"string","principale":"boolean"} -> {"id":"number"}
        await apiFetch(`/api/nominativi/${nominativo.id}/indirizzi`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(indirizzoForm),
        });
      }
      setShowIndirizzoModal(false);
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante il salvataggio dell\'indirizzo');
    } finally {
      setSavingIndirizzo(false);
    }
  };

  const deleteIndirizzo = async (ind: Indirizzo) => {
    if (isUnicaSedeLegale(ind)) {
      alert('Non è possibile eliminare l\'unica Sede Legale del nominativo. Aggiungi prima un\'altra Sede Legale, oppure modifica questa.');
      return;
    }
    if (!window.confirm('Eliminare questo indirizzo?')) return;
    try {
      // @api DELETE /api/nominativi/:id/indirizzi/:indirizzoId -> {"success":"boolean","message":"string"}
      await apiFetch(`/api/nominativi/${nominativo.id}/indirizzi/${ind.id}`, { method: 'DELETE' });
      await onReload();
    } catch (err: any) {
      alert(err.message || 'Errore durante l\'eliminazione');
    }
  };

  return (
    <div className="bop-anagrafica">
      {/* Banner informativo sola lettura */}
      <div className="bop-card bop-card__body bop-text-muted">
        ℹ️ Le sezioni Email, Contatti e Filiali sono in sola lettura in questo tab.
        Per modificarle usa i tab <strong>Cliente</strong> o <strong>Fornitore</strong>.
      </div>

      {/* Dati Generali */}
      <div className="bop-card bop-card__body">
        <h3 className="bop-anagrafica__section-title">Dati Generali</h3>
        <div className="bop-anagrafica__field-grid">
          <div className="bop-anagrafica__field-item">
            <div className="bop-anagrafica__field-label">Tipo Soggetto</div>
            <div className="bop-anagrafica__field-value">
              <span className={`bop-badge ${nominativo.tipoSoggetto === 'AZIENDA' ? 'bop-badge--info' : 'bop-badge--warning'}`}>
                {nominativo.tipoSoggetto}
              </span>
            </div>
          </div>
          {nominativo.tipoSoggetto === 'AZIENDA' ? (
            <div className="bop-anagrafica__field-item">
              <div className="bop-anagrafica__field-label">Ragione Sociale</div>
              <div className="bop-anagrafica__field-value">{nominativo.ragioneSociale || '—'}</div>
            </div>
          ) : (
            <>
              <div className="bop-anagrafica__field-item">
                <div className="bop-anagrafica__field-label">Nome</div>
                <div className="bop-anagrafica__field-value">{nominativo.nome || '—'}</div>
              </div>
              <div className="bop-anagrafica__field-item">
                <div className="bop-anagrafica__field-label">Cognome</div>
                <div className="bop-anagrafica__field-value">{nominativo.cognome || '—'}</div>
              </div>
            </>
          )}
          <div className="bop-anagrafica__field-item">
            <div className="bop-anagrafica__field-label">Partita IVA</div>
            <div className="bop-anagrafica__field-value">{nominativo.partitaIva || '—'}</div>
          </div>
          <div className="bop-anagrafica__field-item">
            <div className="bop-anagrafica__field-label">Codice Fiscale</div>
            <div className="bop-anagrafica__field-value">{nominativo.codiceFiscale || '—'}</div>
          </div>
          <div className="bop-anagrafica__field-item">
            <div className="bop-anagrafica__field-label">Codice SDI</div>
            <div className="bop-anagrafica__field-value">{nominativo.codiceSDI || '—'}</div>
          </div>
          <div className="bop-anagrafica__field-item">
            <div className="bop-anagrafica__field-label">PEC</div>
            <div className="bop-anagrafica__field-value">{nominativo.pec || '—'}</div>
          </div>
          <div className="bop-anagrafica__field-item">
            <div className="bop-anagrafica__field-label">Telefono Principale</div>
            <div className="bop-anagrafica__field-value">{nominativo.telefono || '—'}</div>
          </div>
          <div className="bop-anagrafica__field-item">
            <div className="bop-anagrafica__field-label">Email Principale</div>
            <div className="bop-anagrafica__field-value">{nominativo.emailPrincipale || '—'}</div>
          </div>
          <div className="bop-anagrafica__field-item">
            <div className="bop-anagrafica__field-label">Sito Web</div>
            <div className="bop-anagrafica__field-value">
              {nominativo.sito ? (
                <a href={nominativo.sito} target="_blank" rel="noopener noreferrer" className="bop-anagrafica__link">
                  {nominativo.sito}
                </a>
              ) : '—'}
            </div>
          </div>
        </div>
        {nominativo.note && (
          <div className="bop-anagrafica__field-item">
            <div className="bop-anagrafica__field-label">Note</div>
            <div className="bop-anagrafica__field-value">{nominativo.note}</div>
          </div>
        )}
      </div>

      {/* Indirizzi */}
      <div className="bop-card bop-card__body">
        <h3 className="bop-anagrafica__section-title">
          Indirizzi
          <button className="bop-btn-primary" onClick={() => openIndirizzoModal()}>
            <PlusIcon />
            Aggiungi
          </button>
        </h3>
        {nominativo.indirizzi && nominativo.indirizzi.length > 0 ? (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Nome</th>
                <th>Indirizzo</th>
                <th className="bop-text-center">Principale</th>
                <th className="bop-text-center">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {nominativo.indirizzi.map((ind) => {
                const unica = isUnicaSedeLegale(ind);
                return (
                  <tr key={ind.id}>
                    <td>{getTipoIndirizzoLabel(ind.tipo)}</td>
                    <td className="bop-text-muted">{ind.nome || '—'}</td>
                    <td>{formatIndirizzo(ind)}</td>
                    <td className="bop-text-center">
                      {ind.principale && <span className="bop-badge bop-badge--success">Principale</span>}
                    </td>
                    <td className="bop-text-center">
                      <div className="bop-anagrafica__actions-cell">
                        <button className="bop-btn-secondary" onClick={() => openIndirizzoModal(ind)}>
                          <EditIcon />
                        </button>
                        <button
                          className="bop-anagrafica__btn-delete"
                          onClick={() => deleteIndirizzo(ind)}
                          disabled={unica}
                          title={unica ? 'Non è possibile eliminare l\'unica Sede Legale' : 'Elimina'}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="bop-empty-state">Nessun indirizzo registrato</div>
        )}
      </div>

      {/* Email */}
      <div className="bop-card bop-card__body">
        <h3 className="bop-anagrafica__section-title">Email Secondarie</h3>
        {nominativo.email && nominativo.email.length > 0 ? (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Etichetta</th>
                <th>Email</th>
                <th className="bop-text-center">Ambito</th>
                <th className="bop-text-center">Principale</th>
              </tr>
            </thead>
            <tbody>
              {nominativo.email.map((em) => (
                <tr key={em.id}>
                  <td>{em.etichetta}</td>
                  <td className="bop-text-muted">{em.email}</td>
                  <td className="bop-text-center">
                    {renderAmbitoBadge(em.ambito)}
                  </td>
                  <td className="bop-text-center">
                    {em.principale && <span className="bop-badge bop-badge--success">Principale</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="bop-empty-state">Nessuna email secondaria registrata</div>
        )}
      </div>

      {/* Contatti */}
      <div className="bop-card bop-card__body">
        <h3 className="bop-anagrafica__section-title">Contatti</h3>
        {nominativo.contatti && nominativo.contatti.length > 0 ? (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Ruolo</th>
                <th>Telefono</th>
                <th>Email</th>
                <th className="bop-text-center">Ambito</th>
                <th className="bop-text-center">Principale</th>
              </tr>
            </thead>
            <tbody>
              {nominativo.contatti.map((contatto) => (
                <tr key={contatto.id}>
                  <td>{`${contatto.nome} ${contatto.cognome || ''}`.trim()}</td>
                  <td className="bop-text-muted">{contatto.ruolo || '—'}</td>
                  <td className="bop-text-muted">
                    {contatto.telefono || contatto.cellulare || '—'}
                  </td>
                  <td className="bop-text-muted">{contatto.email || '—'}</td>
                  <td className="bop-text-center">
                    {renderAmbitoBadge(contatto.ambito)}
                  </td>
                  <td className="bop-text-center">
                    {contatto.principale && <span className="bop-badge bop-badge--success">Principale</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="bop-empty-state">Nessun contatto registrato</div>
        )}
      </div>

      {/* Filiali */}
      <div className="bop-card bop-card__body">
        <h3 className="bop-anagrafica__section-title">Filiali / Punti Vendita</h3>
        {nominativo.filiali && nominativo.filiali.length > 0 ? (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Indirizzo</th>
                <th className="bop-text-center">Stato</th>
              </tr>
            </thead>
            <tbody>
              {nominativo.filiali.map((fil) => {
                const indirizzoFiliale = [fil.via, fil.cap, fil.citta, fil.provincia].filter(Boolean).join(' ') || '—';
                return (
                  <tr key={fil.id}>
                    <td>{fil.nome}</td>
                    <td className="bop-text-muted">{indirizzoFiliale}</td>
                    <td className="bop-text-center">
                      <span className={`bop-badge ${fil.attivo ? 'bop-badge--success' : 'bop-badge--danger'}`}>
                        {fil.attivo ? 'Attivo' : 'Disattivato'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="bop-empty-state">Nessuna filiale registrata</div>
        )}
      </div>

      {/* Modal Indirizzo */}
      {showIndirizzoModal && (
        <div className="bop-modal-overlay" onClick={(e) => e.stopPropagation()}>
          <div className="bop-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bop-modal__header">
              <h3 className="bop-modal__title">{editingIndirizzo ? 'Modifica Indirizzo' : 'Nuovo Indirizzo'}</h3>
            </div>
            <div className="bop-modal__body">
              <div className="bop-form-group">
                <label className="bop-label">Tipo *</label>
                <select
                  className="bop-select"
                  value={indirizzoForm.tipo}
                  onChange={(e) => setIndirizzoForm({ ...indirizzoForm, tipo: e.target.value as any })}
                  disabled={savingIndirizzo}
                >
                  <option value="SEDE_LEGALE">Sede Legale</option>
                  <option value="SEDE_OPERATIVA">Sede Operativa</option>
                  <option value="SPEDIZIONE">Spedizione</option>
                </select>
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Etichetta (opzionale)</label>
                <input
                  className="bop-input"
                  value={indirizzoForm.nome}
                  onChange={(e) => setIndirizzoForm({ ...indirizzoForm, nome: e.target.value })}
                  placeholder="es. Magazzino centrale, Filiale Milano, ..."
                  disabled={savingIndirizzo}
                />
              </div>
              <div className="bop-form-group">
                <label className="bop-label">Via e numero civico</label>
                <input
                  className="bop-input"
                  value={indirizzoForm.via}
                  onChange={(e) => setIndirizzoForm({ ...indirizzoForm, via: e.target.value })}
                  disabled={savingIndirizzo}
                />
              </div>
              <div className="bop-anagrafica__form-grid-narrow-wide">
                <div className="bop-form-group">
                  <label className="bop-label">CAP</label>
                  <input
                    className="bop-input"
                    value={indirizzoForm.cap}
                    onChange={(e) => setIndirizzoForm({ ...indirizzoForm, cap: e.target.value })}
                    disabled={savingIndirizzo}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Città *</label>
                  <input
                    className="bop-input"
                    value={indirizzoForm.citta}
                    onChange={(e) => setIndirizzoForm({ ...indirizzoForm, citta: e.target.value })}
                    disabled={savingIndirizzo}
                  />
                </div>
              </div>
              <div className="bop-anagrafica__form-grid-narrow-wide">
                <div className="bop-form-group">
                  <label className="bop-label">Provincia</label>
                  <input
                    className="bop-input"
                    maxLength={2}
                    value={indirizzoForm.provincia}
                    onChange={(e) => setIndirizzoForm({ ...indirizzoForm, provincia: e.target.value.toUpperCase() })}
                    disabled={savingIndirizzo}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Paese</label>
                  <select
                    className="bop-select"
                    value={indirizzoForm.paese}
                    onChange={(e) => setIndirizzoForm({ ...indirizzoForm, paese: e.target.value })}
                    disabled={savingIndirizzo}
                  >
                    {/* Se il valore salvato non è in lista (dati storici a testo libero), lo mostriamo comunque */}
                    {indirizzoForm.paese && !PAESI.some((p) => p.nome === indirizzoForm.paese) && (
                      <option value={indirizzoForm.paese}>{indirizzoForm.paese}</option>
                    )}
                    {PAESI.map((p) => (
                      <option key={p.codice} value={p.nome}>{p.nome}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="bop-form-group">
                <label className="bop-anagrafica__checkbox-label">
                  <input
                    type="checkbox"
                    checked={indirizzoForm.principale}
                    onChange={(e) => setIndirizzoForm({ ...indirizzoForm, principale: e.target.checked })}
                    disabled={savingIndirizzo}
                  />
                  Indirizzo principale
                </label>
              </div>
            </div>
            <div className="bop-modal__footer">
              <button className="bop-btn-secondary" onClick={() => setShowIndirizzoModal(false)} disabled={savingIndirizzo}>
                Annulla
              </button>
              <button className="bop-btn-primary" onClick={saveIndirizzo} disabled={savingIndirizzo}>
                {savingIndirizzo ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ApiInspector docs={API_DOCS} />
    </div>
  );
}
